import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, X, Save, Facebook, Eye, EyeOff, FileSpreadsheet, Mail, Clock, Zap, Server, Key, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contactSources, setContactSources] = useState([]);
  const [campaignTypes, setCampaignTypes] = useState([]);
  const [contactInterets, setContactInterets] = useState([]);
  const [contactStatuts, setContactStatuts] = useState([]);
  const [contactDegresInteret, setContactDegresInteret] = useState([]);
  const [newSource, setNewSource] = useState('');
  const [newType, setNewType] = useState('');
  const [newInteret, setNewInteret] = useState('');
  const [newStatut, setNewStatut] = useState('');
  const [newDegreInteret, setNewDegreInteret] = useState('');
  
  // Configuration SMTP
  const [smtpServer, setSmtpServer] = useState('');
  const [smtpPort, setSmtpPort] = useState('465');
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFrom, setSmtpFrom] = useState('');
  const [smtpFromName, setSmtpFromName] = useState('');
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  
  // Configuration Facebook
  const [facebookPageId, setFacebookPageId] = useState('');
  const [facebookAccessToken, setFacebookAccessToken] = useState('');
  const [facebookPageUrl, setFacebookPageUrl] = useState('');
  const [showToken, setShowToken] = useState(false);
  
  // Valeurs par défaut pour l'importation Excel
  const [importDefaultStatut, setImportDefaultStatut] = useState('');
  const [importDefaultSource, setImportDefaultSource] = useState('');
  const [importDefaultDegreInteret, setImportDefaultDegreInteret] = useState('');
  
  // Configuration Throttling pour l'envoi d'emails
  const [emailThrottleEnabled, setEmailThrottleEnabled] = useState(true);
  const [emailBatchSize, setEmailBatchSize] = useState(50);
  const [emailBatchDelaySeconds, setEmailBatchDelaySeconds] = useState(1800);
  const [emailMaxPerHour, setEmailMaxPerHour] = useState(100);
  
  // Configuration des rappels automatiques de rendez-vous
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [reminderHoursBefore, setReminderHoursBefore] = useState(24);
  const [reminderSendToTeam, setReminderSendToTeam] = useState(true);
  const [reminderCheckIntervalMinutes, setReminderCheckIntervalMinutes] = useState(30);

  useEffect(() => {
    fetchSettings();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/app`, getAuthHeaders());
      setContactSources(response.data.contact_sources || []);
      setCampaignTypes(response.data.campaign_types || []);
      setContactInterets(response.data.contact_interets || []);
      setContactStatuts(response.data.contact_statuts || []);
      setContactDegresInteret(response.data.contact_degres_interet || []);
      // Configuration Facebook
      setFacebookPageId(response.data.facebook_page_id || '');
      setFacebookAccessToken(response.data.facebook_access_token || '');
      setFacebookPageUrl(response.data.facebook_page_url || '');
      // Valeurs par défaut pour l'importation Excel
      setImportDefaultStatut(response.data.import_default_statut || '');
      setImportDefaultSource(response.data.import_default_source || '');
      setImportDefaultDegreInteret(response.data.import_default_degre_interet || '');
      // Configuration Throttling pour l'envoi d'emails
      setEmailThrottleEnabled(response.data.email_throttle_enabled !== false);
      setEmailBatchSize(response.data.email_batch_size || 50);
      setEmailBatchDelaySeconds(response.data.email_batch_delay_seconds || 1800);
      setEmailMaxPerHour(response.data.email_max_per_hour || 100);
      // Configuration des rappels automatiques de rendez-vous
      setReminderEnabled(response.data.reminder_enabled !== false);
      setReminderHoursBefore(response.data.reminder_hours_before || 24);
      setReminderSendToTeam(response.data.reminder_send_to_team !== false);
      setReminderCheckIntervalMinutes(response.data.reminder_check_interval_minutes || 30);
      // Configuration SMTP
      setSmtpServer(response.data.smtp_server || '');
      setSmtpPort(response.data.smtp_port?.toString() || '465');
      setSmtpUsername(response.data.smtp_username || '');
      setSmtpPassword(response.data.smtp_password || '');
      setSmtpFrom(response.data.smtp_from || '');
      setSmtpFromName(response.data.smtp_from_name || 'Refacing VR');
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
      toast.error('Erreur lors du chargement des paramètres');
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/settings/app`,
        {
          contact_sources: contactSources,
          campaign_types: campaignTypes,
          contact_interets: contactInterets,
          contact_statuts: contactStatuts,
          contact_degres_interet: contactDegresInteret,
          facebook_page_id: facebookPageId,
          facebook_access_token: facebookAccessToken,
          facebook_page_url: facebookPageUrl,
          import_default_statut: importDefaultStatut,
          import_default_source: importDefaultSource,
          import_default_degre_interet: importDefaultDegreInteret,
          email_throttle_enabled: emailThrottleEnabled,
          email_batch_size: emailBatchSize,
          email_batch_delay_seconds: emailBatchDelaySeconds,
          email_max_per_hour: emailMaxPerHour,
          reminder_enabled: reminderEnabled,
          reminder_hours_before: reminderHoursBefore,
          reminder_send_to_team: reminderSendToTeam,
          reminder_check_interval_minutes: reminderCheckIntervalMinutes,
          // Configuration SMTP
          smtp_server: smtpServer,
          smtp_port: parseInt(smtpPort) || 465,
          smtp_username: smtpUsername,
          smtp_password: smtpPassword,
          smtp_from: smtpFrom,
          smtp_from_name: smtpFromName
        },
        getAuthHeaders()
      );
      toast.success('Paramètres sauvegardés avec succès');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const testSmtpConnection = async () => {
    if (!smtpServer || !smtpUsername || !smtpPassword) {
      toast.error('Veuillez remplir tous les champs SMTP avant de tester');
      return;
    }
    
    const testEmail = prompt('Entrez une adresse email pour recevoir le test:', smtpUsername);
    if (!testEmail) return;
    
    setTestingSmtp(true);
    setSmtpTestResult(null);
    
    try {
      // D'abord sauvegarder les paramètres
      await handleSave();
      
      // Puis tester
      const response = await axios.post(
        `${API}/settings/test-smtp`,
        { test_email: testEmail },
        getAuthHeaders()
      );
      
      setSmtpTestResult(response.data);
      
      if (response.data.success) {
        toast.success('Test SMTP réussi! Vérifiez votre boîte de réception.');
      } else {
        toast.error(response.data.error || 'Test SMTP échoué');
      }
    } catch (error) {
      console.error('Erreur test SMTP:', error);
      setSmtpTestResult({
        success: false,
        error: error.response?.data?.error || 'Erreur lors du test'
      });
      toast.error('Erreur lors du test SMTP');
    } finally {
      setTestingSmtp(false);
    }
  };

  const addSource = () => {
    if (!newSource.trim()) {
      toast.error('Veuillez entrer un nom de source');
      return;
    }
    if (contactSources.includes(newSource.trim())) {
      toast.error('Cette source existe déjà');
      return;
    }
    setContactSources([...contactSources, newSource.trim()]);
    setNewSource('');
  };

  const removeSource = (source) => {
    setContactSources(contactSources.filter(s => s !== source));
  };

  const addType = () => {
    if (!newType.trim()) {
      toast.error('Veuillez entrer un nom de type');
      return;
    }
    if (campaignTypes.includes(newType.trim())) {
      toast.error('Ce type existe déjà');
      return;
    }
    setCampaignTypes([...campaignTypes, newType.trim()]);
    setNewType('');
  };

  const removeType = (type) => {
    setCampaignTypes(campaignTypes.filter(t => t !== type));
  };

  const addInteret = () => {
    if (!newInteret.trim()) {
      toast.error('Veuillez entrer un nom d\'intérêt');
      return;
    }
    if (contactInterets.includes(newInteret.trim())) {
      toast.error('Cet intérêt existe déjà');
      return;
    }
    setContactInterets([...contactInterets, newInteret.trim()]);
    setNewInteret('');
  };

  const removeInteret = (interet) => {
    setContactInterets(contactInterets.filter(i => i !== interet));
  };

  const addStatut = () => {
    if (!newStatut.trim()) {
      toast.error('Veuillez entrer un nom de statut');
      return;
    }
    if (contactStatuts.includes(newStatut.trim())) {
      toast.error('Ce statut existe déjà');
      return;
    }
    setContactStatuts([...contactStatuts, newStatut.trim()]);
    setNewStatut('');
  };

  const removeStatut = (statut) => {
    setContactStatuts(contactStatuts.filter(s => s !== statut));
  };

  const addDegreInteret = () => {
    if (!newDegreInteret.trim()) {
      toast.error('Veuillez entrer un degré d\'intérêt');
      return;
    }
    if (contactDegresInteret.includes(newDegreInteret.trim())) {
      toast.error('Ce degré existe déjà');
      return;
    }
    setContactDegresInteret([...contactDegresInteret, newDegreInteret.trim()]);
    setNewDegreInteret('');
  };

  const removeDegreInteret = (degre) => {
    setContactDegresInteret(contactDegresInteret.filter(d => d !== degre));
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: '#F5F5F0' }}>
        <div className="container mx-auto p-6">
          <p className="text-center">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F5F5F0' }}>
      <div className="container mx-auto p-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 rounded-lg hover:bg-gray-200"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-3xl font-bold" style={{ color: '#333333' }}>
              Paramètres de l'Application
            </h1>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
            style={{ background: '#335C2E' }}
          >
            <Save size={20} />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>

        {/* Sources de contacts */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#333333' }}>
            Sources de Contacts
          </h2>
          <p className="text-gray-600 mb-4">
            Gérez les sources disponibles lors de la création ou modification de contacts dans le CRM.
          </p>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newSource}
              onChange={(e) => setNewSource(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addSource()}
              placeholder="Nouvelle source (ex: LinkedIn, Salon)"
              className="flex-1 px-4 py-2 border rounded-lg"
            />
            <button
              onClick={addSource}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90"
              style={{ background: '#335C2E' }}
            >
              <Plus size={18} />
              Ajouter
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {contactSources.map((source) => (
              <div
                key={source}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg"
              >
                <span>{source}</span>
                <button
                  onClick={() => removeSource(source)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Types de campagnes */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#333333' }}>
            Types de Campagnes
          </h2>
          <p className="text-gray-600 mb-4">
            Gérez les types de campagnes disponibles lors de la création de campagnes d'emailing.
          </p>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addType()}
              placeholder="Nouveau type (ex: Bienvenue, Abandon panier)"
              className="flex-1 px-4 py-2 border rounded-lg"
            />
            <button
              onClick={addType}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90"
              style={{ background: '#335C2E' }}
            >
              <Plus size={18} />
              Ajouter
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {campaignTypes.map((type) => (
              <div
                key={type}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg"
              >
                <span>{type}</span>
                <button
                  onClick={() => removeType(type)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Intérêts des contacts */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#333333' }}>
            Intérêts des Contacts
          </h2>
          <p className="text-gray-600 mb-4">
            Gérez les intérêts disponibles pour qualifier les contacts (projets qui les intéressent).
          </p>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newInteret}
              onChange={(e) => setNewInteret(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addInteret()}
              placeholder="Nouvel intérêt (ex: Extension, Piscine)"
              className="flex-1 px-4 py-2 border rounded-lg"
            />
            <button
              onClick={addInteret}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90"
              style={{ background: '#335C2E' }}
            >
              <Plus size={18} />
              Ajouter
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {contactInterets.map((interet) => (
              <div
                key={interet}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg"
              >
                <span>{interet}</span>
                <button
                  onClick={() => removeInteret(interet)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Statuts des contacts */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#333333' }}>
            Statuts des Contacts
          </h2>
          <p className="text-gray-600 mb-4">
            Gérez les statuts disponibles pour qualifier l'état d'avancement des contacts.
          </p>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newStatut}
              onChange={(e) => setNewStatut(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addStatut()}
              placeholder="Nouveau statut (ex: Qualifié, En négociation)"
              className="flex-1 px-4 py-2 border rounded-lg"
            />
            <button
              onClick={addStatut}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90"
              style={{ background: '#335C2E' }}
            >
              <Plus size={18} />
              Ajouter
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {contactStatuts.map((statut) => (
              <div
                key={statut}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg"
              >
                <span>{statut}</span>
                <button
                  onClick={() => removeStatut(statut)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Degrés d'intérêt */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#333333' }}>
            Degrés d'Intérêt
          </h2>
          <p className="text-gray-600 mb-4">
            Gérez les niveaux d'intérêt pour évaluer la température des prospects.
          </p>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newDegreInteret}
              onChange={(e) => setNewDegreInteret(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addDegreInteret()}
              placeholder="Nouveau degré (ex: Très chaud, À relancer)"
              className="flex-1 px-4 py-2 border rounded-lg"
            />
            <button
              onClick={addDegreInteret}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90"
              style={{ background: '#335C2E' }}
            >
              <Plus size={18} />
              Ajouter
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {contactDegresInteret.map((degre) => (
              <div
                key={degre}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg"
              >
                <span>{degre}</span>
                <button
                  onClick={() => removeDegreInteret(degre)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Valeurs par défaut pour l'importation Excel */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#335C2E' }}>
            <FileSpreadsheet size={24} />
            Valeurs par défaut pour l'importation Excel
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Définissez les valeurs par défaut qui seront attribuées aux contacts importés lorsque certains champs sont manquants dans le fichier Excel.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Statut par défaut */}
            <div>
              <label className="block text-sm font-medium mb-1">Statut par défaut</label>
              <select
                value={importDefaultStatut}
                onChange={(e) => setImportDefaultStatut(e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">-- Sélectionner un statut --</option>
                {contactStatuts.map((statut) => (
                  <option key={statut} value={statut}>{statut}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Statut attribué quand la colonne est vide
              </p>
            </div>
            
            {/* Source par défaut */}
            <div>
              <label className="block text-sm font-medium mb-1">Source par défaut</label>
              <select
                value={importDefaultSource}
                onChange={(e) => setImportDefaultSource(e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">-- Sélectionner une source --</option>
                {contactSources.map((source) => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Source attribuée quand la colonne est vide
              </p>
            </div>
            
            {/* Degré d'intérêt par défaut */}
            <div>
              <label className="block text-sm font-medium mb-1">Degré d'intérêt par défaut</label>
              <select
                value={importDefaultDegreInteret}
                onChange={(e) => setImportDefaultDegreInteret(e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">-- Sélectionner un degré --</option>
                {contactDegresInteret.map((degre) => (
                  <option key={degre} value={degre}>{degre}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Degré attribué quand la colonne est vide
              </p>
            </div>
          </div>
          
          {/* Message d'information */}
          <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-800">
              <strong>💡 Astuce :</strong> Ces valeurs seront automatiquement utilisées lors de l'importation de contacts via fichier Excel lorsque les colonnes <em>Statut</em>, <em>Source</em> ou <em>Degré d'intérêt</em> sont absentes ou vides.
            </p>
          </div>
          
          {/* Indicateur de statut */}
          <div className="mt-4 flex items-center gap-2">
            {importDefaultStatut && importDefaultSource && importDefaultDegreInteret ? (
              <span className="flex items-center gap-2 px-3 py-1 rounded-full text-sm" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Configuration complète
              </span>
            ) : (
              <span className="flex items-center gap-2 px-3 py-1 rounded-full text-sm" style={{ background: '#FFF3E0', color: '#E65100' }}>
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Configuration incomplète - Certaines valeurs par défaut ne sont pas définies
              </span>
            )}
          </div>
        </div>

        {/* Configuration Facebook */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#1877F2' }}>
            <Facebook size={24} />
            Configuration Facebook
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Configurez l'accès à votre page Facebook pour permettre le partage automatique des articles de blog.
          </p>
          
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">ID de la page Facebook *</label>
              <input
                type="text"
                value={facebookPageId}
                onChange={(e) => setFacebookPageId(e.target.value)}
                placeholder="Ex: 123456789012345"
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                L'ID numérique de votre page Facebook (visible dans les paramètres de la page)
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Token d'accès Facebook *</label>
              <div className="relative">
                <input
                  type={showToken ? "text" : "password"}
                  value={facebookAccessToken}
                  onChange={(e) => setFacebookAccessToken(e.target.value)}
                  placeholder="Votre token d'accès à la page"
                  className="w-full p-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showToken ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Token d'accès de longue durée avec permissions pages_manage_posts et pages_read_engagement
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">URL de la page Facebook</label>
              <input
                type="text"
                value={facebookPageUrl}
                onChange={(e) => setFacebookPageUrl(e.target.value)}
                placeholder="Ex: https://www.facebook.com/votrepagefacebook"
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                L'URL publique de votre page Facebook (optionnel, pour les liens)
              </p>
            </div>
          </div>
          
          {/* Instructions détaillées */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-medium text-blue-800 mb-2">📘 Comment obtenir ces informations ?</h3>
            <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
              <li>
                <strong>ID de page :</strong> Allez sur votre page Facebook → Paramètres → Informations sur la Page → ID de la page
              </li>
              <li>
                <strong>Token d'accès :</strong>
                <ul className="ml-6 mt-1 space-y-1 list-disc">
                  <li>Créez une application sur <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="underline">developers.facebook.com</a></li>
                  <li>Ajoutez le produit "Facebook Login" et "Pages API"</li>
                  <li>Générez un token de page avec les permissions : <code className="bg-blue-100 px-1 rounded">pages_manage_posts</code>, <code className="bg-blue-100 px-1 rounded">pages_read_engagement</code></li>
                  <li>Échangez le token court pour un token de longue durée (60 jours)</li>
                </ul>
              </li>
            </ol>
          </div>
          
          {/* Indicateur de statut */}
          <div className="mt-4 flex items-center gap-2">
            {facebookPageId && facebookAccessToken ? (
              <span className="flex items-center gap-2 px-3 py-1 rounded-full text-sm" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Configuration complète
              </span>
            ) : (
              <span className="flex items-center gap-2 px-3 py-1 rounded-full text-sm" style={{ background: '#FFF3E0', color: '#E65100' }}>
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Configuration incomplète - Le partage Facebook ne fonctionnera pas
              </span>
            )}
          </div>
        </div>

        {/* Configuration Throttling Email */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#335C2E' }}>
            <Mail size={24} />
            Limite d'envoi d'emails (Throttling)
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Configurez les limites d'envoi pour respecter les quotas de votre hébergeur et éviter les blocages.
          </p>
          
          {/* Toggle Throttling */}
          <div className="mb-4 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Activer le throttling</p>
              <p className="text-sm text-gray-500">Envoyer les emails par lots avec délais</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={emailThrottleEnabled}
                onChange={(e) => setEmailThrottleEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
          
          {emailThrottleEnabled && (
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              {/* Limite hébergeur */}
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                  <Mail size={16} />
                  Limite de votre hébergeur (emails/heure)
                </label>
                <input
                  type="number"
                  min="50"
                  max="1000"
                  value={emailMaxPerHour}
                  onChange={(e) => setEmailMaxPerHour(parseInt(e.target.value) || 100)}
                  className="w-full max-w-xs p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Limite imposée par votre hébergeur (généralement 100 pour cPanel/Likuid)
                </p>
              </div>
            </div>
          )}
          
          {/* Exemple de calcul */}
          {emailThrottleEnabled && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800">
                <strong>📊 Comment ça fonctionne :</strong>
              </p>
              <p className="text-sm text-green-700 mt-2">
                Avec une limite de <strong>{emailMaxPerHour} emails/heure</strong>, le système envoie 
                automatiquement <strong>1 email toutes les {Math.round(3600 / emailMaxPerHour * 1.2)} secondes</strong> (avec marge de sécurité de 20%).
              </p>
              <ul className="text-sm text-green-700 mt-2 space-y-1 ml-4 list-disc">
                <li><strong>100 contacts</strong> → ~{Math.round(100 * 3600 / emailMaxPerHour * 1.2 / 60)} minutes ({Math.round(100 * 3600 / emailMaxPerHour * 1.2 / 3600 * 10) / 10}h)</li>
                <li><strong>500 contacts</strong> → ~{Math.round(500 * 3600 / emailMaxPerHour * 1.2 / 60)} minutes ({Math.round(500 * 3600 / emailMaxPerHour * 1.2 / 3600 * 10) / 10}h)</li>
                <li><strong>1000 contacts</strong> → ~{Math.round(1000 * 3600 / emailMaxPerHour * 1.2 / 60)} minutes ({Math.round(1000 * 3600 / emailMaxPerHour * 1.2 / 3600 * 10) / 10}h)</li>
              </ul>
              <p className="text-xs text-green-600 mt-2">
                ⏸️ L'envoi peut être mis en pause et repris à tout moment. La progression est sauvegardée.
              </p>
            </div>
          )}
          
          {!emailThrottleEnabled && (
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Attention :</strong> Le throttling est désactivé. Les emails seront envoyés aussi vite que possible, 
                ce qui peut dépasser la limite de votre hébergeur et entraîner des blocages.
              </p>
            </div>
          )}
        </div>

        {/* Configuration Rappels Automatiques RDV */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#335C2E' }}>
            <Clock size={24} />
            Rappels automatiques de rendez-vous
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Configurez l'envoi automatique de rappels par email avant les rendez-vous.
          </p>
          
          {/* Toggle Rappels */}
          <div className="mb-4 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Activer les rappels automatiques</p>
              <p className="text-sm text-gray-500">Envoyer des rappels par email aux clients avant leurs RDV</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={reminderEnabled}
                onChange={(e) => setReminderEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
          
          {reminderEnabled && (
            <div className="space-y-4">
              {/* Délai avant le RDV */}
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                  <Clock size={16} />
                  Délai avant le rendez-vous
                </label>
                <select
                  value={reminderHoursBefore}
                  onChange={(e) => setReminderHoursBefore(parseInt(e.target.value))}
                  className="w-full max-w-xs p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value={6}>6 heures avant</option>
                  <option value={12}>12 heures avant</option>
                  <option value={24}>24 heures avant (1 jour)</option>
                  <option value={48}>48 heures avant (2 jours)</option>
                  <option value={72}>72 heures avant (3 jours)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Le rappel sera envoyé {reminderHoursBefore} heures avant le rendez-vous
                </p>
              </div>
              
              {/* Toggle notification équipe */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Notifier l'équipe</p>
                  <p className="text-sm text-gray-500">Envoyer aussi un rappel à l'équipe Refacing VR</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reminderSendToTeam}
                    onChange={(e) => setReminderSendToTeam(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
              
              {/* Intervalle de vérification */}
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                  <Zap size={16} />
                  Fréquence de vérification
                </label>
                <select
                  value={reminderCheckIntervalMinutes}
                  onChange={(e) => setReminderCheckIntervalMinutes(parseInt(e.target.value))}
                  className="w-full max-w-xs p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value={15}>Toutes les 15 minutes</option>
                  <option value={30}>Toutes les 30 minutes</option>
                  <option value={60}>Toutes les heures</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Le système vérifie les RDV à rappeler toutes les {reminderCheckIntervalMinutes} minutes
                </p>
              </div>
            </div>
          )}
          
          {/* Info box */}
          {reminderEnabled && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800">
                <strong>📧 Fonctionnement :</strong>
              </p>
              <ul className="text-sm text-green-700 mt-2 space-y-1 ml-4 list-disc">
                <li>Les rappels sont envoyés automatiquement <strong>{reminderHoursBefore}h</strong> avant chaque RDV confirmé</li>
                <li>Seuls les RDV avec statut <strong>"Confirmé"</strong> reçoivent un rappel</li>
                <li>Chaque RDV ne reçoit qu'<strong>un seul rappel</strong> automatique</li>
                {reminderSendToTeam && <li>L'équipe reçoit aussi une <strong>notification interne</strong></li>}
              </ul>
            </div>
          )}
          
          {!reminderEnabled && (
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <strong>ℹ️ Rappels désactivés :</strong> Vous pouvez toujours envoyer des rappels manuellement 
                depuis la gestion des rendez-vous en cliquant sur l'icône 🔔.
              </p>
            </div>
          )}
        </div>

        {/* Configuration SMTP */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#335C2E' }}>
            <Server size={24} />
            Configuration Email (SMTP)
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Configurez les paramètres de votre serveur SMTP pour l'envoi d'emails (campagnes, notifications, rappels).
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Serveur SMTP */}
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                <Server size={16} />
                Serveur SMTP *
              </label>
              <input
                type="text"
                value={smtpServer}
                onChange={(e) => setSmtpServer(e.target.value)}
                placeholder="Ex: smtp.gmail.com, smtp.office365.com"
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Serveur de messagerie sortante
              </p>
            </div>
            
            {/* Port SMTP */}
            <div>
              <label className="block text-sm font-medium mb-1">Port *</label>
              <select
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="465">465 (SSL - recommandé)</option>
                <option value="587">587 (TLS/STARTTLS)</option>
                <option value="25">25 (non sécurisé)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Port 465 pour la plupart des hébergeurs
              </p>
            </div>
            
            {/* Nom d'utilisateur SMTP */}
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                <Mail size={16} />
                Nom d'utilisateur / Email *
              </label>
              <input
                type="text"
                value={smtpUsername}
                onChange={(e) => setSmtpUsername(e.target.value)}
                placeholder="Ex: info@votredomaine.com"
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Généralement votre adresse email complète
              </p>
            </div>
            
            {/* Mot de passe SMTP */}
            <div>
              <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                <Key size={16} />
                Mot de passe *
              </label>
              <div className="relative">
                <input
                  type={showSmtpPassword ? "text" : "password"}
                  value={smtpPassword}
                  onChange={(e) => setSmtpPassword(e.target.value)}
                  placeholder="Mot de passe ou mot de passe d'application"
                  className="w-full p-2 pr-10 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <button
                  type="button"
                  onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSmtpPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Pour Gmail/Outlook, utilisez un "mot de passe d'application"
              </p>
            </div>
            
            {/* Email expéditeur */}
            <div>
              <label className="block text-sm font-medium mb-1">Email expéditeur</label>
              <input
                type="email"
                value={smtpFrom}
                onChange={(e) => setSmtpFrom(e.target.value)}
                placeholder="Ex: info@refacingvr.com (optionnel)"
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si différent du nom d'utilisateur (optionnel)
              </p>
            </div>
            
            {/* Nom affiché */}
            <div>
              <label className="block text-sm font-medium mb-1">Nom affiché</label>
              <input
                type="text"
                value={smtpFromName}
                onChange={(e) => setSmtpFromName(e.target.value)}
                placeholder="Ex: Refacing VR"
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Nom affiché dans les emails envoyés
              </p>
            </div>
          </div>
          
          {/* Bouton de test */}
          <div className="mt-6 flex items-center gap-4">
            <button
              onClick={testSmtpConnection}
              disabled={testingSmtp || !smtpServer || !smtpUsername || !smtpPassword}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                background: testingSmtp ? '#6B7280' : '#335C2E', 
                color: 'white' 
              }}
              data-testid="test-smtp-btn"
            >
              {testingSmtp ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Test en cours...
                </>
              ) : (
                <>
                  <Mail size={18} />
                  Tester la connexion SMTP
                </>
              )}
            </button>
            
            {smtpTestResult && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                smtpTestResult.success 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {smtpTestResult.success ? (
                  <>
                    <CheckCircle size={18} />
                    {smtpTestResult.message || 'Test réussi!'}
                  </>
                ) : (
                  <>
                    <AlertTriangle size={18} />
                    {smtpTestResult.error || 'Échec du test'}
                  </>
                )}
              </div>
            )}
          </div>
          
          {/* Afficher les suggestions si erreur */}
          {smtpTestResult && !smtpTestResult.success && smtpTestResult.suggestions && (
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm font-medium text-yellow-800 mb-2">💡 Suggestions :</p>
              <ul className="text-sm text-yellow-700 space-y-1 ml-4 list-disc">
                {smtpTestResult.suggestions.map((suggestion, idx) => (
                  <li key={idx}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Instructions détaillées */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="font-medium text-gray-800 mb-2">📘 Configuration selon votre fournisseur</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
              <div>
                <p className="font-semibold">Gmail:</p>
                <ul className="ml-4 list-disc space-y-1">
                  <li>Serveur: <code className="bg-gray-200 px-1 rounded">smtp.gmail.com</code></li>
                  <li>Port: <code className="bg-gray-200 px-1 rounded">465</code> (SSL)</li>
                  <li>Créez un <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">mot de passe d'application</a></li>
                </ul>
              </div>
              <div>
                <p className="font-semibold">Outlook/Office 365:</p>
                <ul className="ml-4 list-disc space-y-1">
                  <li>Serveur: <code className="bg-gray-200 px-1 rounded">smtp.office365.com</code></li>
                  <li>Port: <code className="bg-gray-200 px-1 rounded">587</code> (TLS)</li>
                  <li>Activez l'authentification à deux facteurs</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Indicateur de statut */}
          <div className="mt-4 flex items-center gap-2">
            {smtpServer && smtpUsername && smtpPassword ? (
              <span className="flex items-center gap-2 px-3 py-1 rounded-full text-sm" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Configuration complète
              </span>
            ) : (
              <span className="flex items-center gap-2 px-3 py-1 rounded-full text-sm" style={{ background: '#FFF3E0', color: '#E65100' }}>
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Configuration incomplète - L'envoi d'emails ne fonctionnera pas
              </span>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Note :</strong> Ces paramètres affectent les options disponibles dans les formulaires de création/modification de contacts et campagnes. Les valeurs existantes ne seront pas affectées.
          </p>
        </div>

        {/* Migration stockage */}
        <div className="mt-6 p-6 bg-white rounded-xl shadow-sm border">
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#335C2E' }}>Stockage persistant des fichiers</h3>
          <p className="text-sm text-gray-600 mb-4">
            Migrer les images locales (blog, newsletter, bibliothèque) vers le stockage cloud persistant. 
            Les images survivront aux mises à jour et redéploiements.
          </p>
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem('admin_token');
                const res = await axios.post(`${API}/api/storage/migrate-local`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                alert(`Migration terminée: ${res.data.migrated} fichiers migrés (${res.data.errors} erreurs)`);
              } catch (err) {
                alert('Erreur de migration: ' + (err.response?.data?.detail || err.message));
              }
            }}
            className="px-4 py-2 text-white rounded-lg text-sm font-medium"
            style={{ backgroundColor: '#335C2E' }}
            data-testid="migrate-storage-btn"
          >
            Migrer les fichiers vers le stockage cloud
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppSettings;
